import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ErrorBoundary from '../../components/ErrorBoundary.jsx';

const fmtQty = (q) => Number(q || 0).toLocaleString('en-US', { maximumFractionDigits: 3 });
const fmtMoney = (c) => `$${Number(c || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * One row per difference of a jobq-api re-check answer (POST /coating/manifests/{id}/recheck, or a Send's 409
 * paper_differs): {key, job, part, paper, packed, pallets, note}. A line whose count is right but names the
 * wrong pallet reads "names X".
 */
export function paperDiffRows(check) {
  const lines = (check && check.lines) || [];
  const missing = (check && check.missing) || [];
  const out = lines.map((d) => {
    const samePieces = Math.abs(Number(d.paper_qty) - Number(d.packed_qty)) < 1e-6;
    const note = Number(d.packed_qty) <= 1e-9 ? (d.why || 'none packed for this order now')
      : samePieces ? `the paper names ${(d.paper_pallets || []).join(', ') || 'no pallet'}`
        : (d.why || '');
    return { key: `l${d.opo_line_id}`, job: d.job, part: d.part, paper: d.paper_qty, packed: d.packed_qty,
      pallets: (d.packed_pallets || []).join(', '), note };
  });
  missing.forEach((m) => out.push({ key: `m${m.job_task_id}`, job: m.job, part: m.part, paper: null, packed: m.qty,
    pallets: (m.pallets || []).join(', '),
    note: m.rider ? "on no paper: another order's job riding this pallet" : 'on no paper' }));
  return out;
}

/**
 * The Send re-check's differences, before the pallet checklist (2026-10-29: KC-SVC-2026-0027 was drafted with each
 * job's full quantity and without job 10136's 62 riders). Lists what the paper says against what the packing plan
 * packs, the totals now and fixed, and offers "Fix the paper and send". Renders in its own React root above any
 * modal, inside an ErrorBoundary (like pinPrompt). Resolves 'fix', 'asis' ("Send as drafted": a manager's PIN and a
 * reason follow, jobq-api logs it) or false (Cancel, X, Escape). A paper with nothing left to send (`fixable`
 * false) offers no fix.
 */
export default function paperDiffPrompt(check, opoNumber) {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);
    let settled = false;

    function finish(value) {
      if (settled) return;
      settled = true;
      root.unmount();
      container.remove();
      resolve(value);
    }

    root.render(
      <ErrorBoundary
        fallback={(error) => (
          <div className="alert alert-danger m-3" role="alert" style={{ position: 'fixed', zIndex: 2001, top: 0, left: 0, right: 0 }}>
            The paper check hit an error: {(error && error.message) || String(error)}. Nothing was sent.
            <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={() => finish(false)}>Close</button>
          </div>
        )}
      >
        <PaperDiffModal check={check} opoNumber={opoNumber} onFix={() => finish('fix')} onAsIs={() => finish('asis')}
          onCancel={() => finish(false)} />
      </ErrorBoundary>
    );
  });
}

export function PaperDiffModal({ check, opoNumber, onFix, onAsIs = null, onCancel }) {
  const [show, setShow] = useState(true);
  const rows = paperDiffRows(check);
  // warnings about a job already in the table (its why, a rider's "added" note) are not repeated under it
  const jobs = new Set(rows.map((r) => String(r.job)));
  const otherWarnings = ((check && check.warnings) || []).filter((w) => {
    const m = /^Job #?(\d+)\b/.exec(w);
    return !(m && jobs.has(m[1]));
  });
  const fixable = check && check.fixable !== false;
  const paper = (check && check.paper) || {};
  const fixed = (check && check.fixed) || {};
  const close = (fn) => () => {
    setShow(false);
    fn();
  };
  return (
    <Modal show={show} onHide={close(onCancel)} backdrop="static" centered size="lg" style={{ zIndex: 2000 }}
      backdropClassName="paperDiffBackdrop" aria-labelledby="paper-diff-title">
      <style>{'.paperDiffBackdrop { z-index: 1999 !important; }'}</style>
      <Modal.Header closeButton>
        <Modal.Title id="paper-diff-title" style={{ fontSize: '1rem' }}>
          {opoNumber || (check && check.opo_number)} doesn&apos;t match its pallets
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small mb-2">
          The packing plan packs something different from what this paper says. Sent as it is, the coater would be told
          the wrong count.
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-sm small mb-2" data-testid="paper-diff-table">
            <thead>
              <tr><th>Job</th><th>Part</th><th className="text-end">On paper</th><th className="text-end">Packed</th>
                <th>Pallet</th><th>Why</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} data-testid="paper-diff-row">
                  <td>{r.job}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{r.part || ''}</td>
                  <td className="text-end">{r.paper == null ? '—' : fmtQty(r.paper)}</td>
                  <td className="text-end fw-bold">{fmtQty(r.packed)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{r.pallets}</td>
                  <td className="text-muted">{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {otherWarnings.length > 0 && (
          <ul className="small text-muted mb-2" data-testid="paper-diff-warnings">
            {otherWarnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        )}
        {fixable ? (
          <div className="small" data-testid="paper-diff-totals">
            Paper now: {fmtQty(paper.qty)} pcs · {fmtMoney(paper.cost)} → fixed: <strong>{fmtQty(fixed.qty)} pcs ·{' '}
            {fmtMoney(fixed.cost)}</strong>
          </div>
        ) : (
          <div className="small text-danger" data-testid="paper-diff-totals">
            Nothing on this paper is left to send. Delete the Draft (make the paper again from To send if anything is
            still to go).
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={close(onCancel)}>{fixable ? 'Cancel' : 'Close'}</Button>
        {onAsIs && (
          <Button variant="outline-secondary" size="sm" onClick={close(onAsIs)} data-testid="paper-diff-asis"
            title="A manager's PIN and a reason: the paper goes out as it is, and JobQ logs the differences">
            Send as drafted (manager)
          </Button>
        )}
        {fixable && (
          <Button variant="primary" size="sm" onClick={close(onFix)} data-testid="paper-diff-fix">
            Fix the paper and send
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
